import { hasMany, belongsTo, attr } from '@ember-data/model';
import ShModel from './sh-model';
import { bool, not } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { formatUri, isUrl, parseUri } from "../utils/misc";
import { htmlSafe } from '@ember/template';

export default ShModel.extend({
  /**
   * Register services
   */
  videoThumbnailService: service('video-thumbnail-from-url'),

  badLink: attr('boolean'),
  confirm: attr('boolean', {defaultValue: false}),
  createdAt: attr('date'),
  rejected: attr('boolean', {defaultValue: false}),
  description: attr('string'),
  editedAt: attr('date'),
  deletedAt: attr('date'),
  embed: attr('string'),
  link: attr('string'),
  pinned: attr('boolean', {defaultValue: false}),
  rank: attr('number'),
  title: attr('string'),
  featured: attr('boolean', {defaultValue: false}),
  thumbnailUrl: attr('string'),
  /**
   * Can only be populated for projects that the currentUser owns
   */
  verifierEmail: attr('string'),

  application: belongsTo('application', { async: false }),
  brand: belongsTo('brand', { async: false }),
  classifications: hasMany('classification', { async: false }),
  company: belongsTo('company', { async: false }),
  image: belongsTo('image', { async: false }),
  oembed: belongsTo('oembed', { async: false }),
  projectdigestVerifications: hasMany('projectdigestVerification', { aync: false }),
  review: belongsTo('project-review', { async: false }),
  services: hasMany('service', { async: false }),
  videoTopics: hasMany('video-topic', { async: false }),
  videoCategory: belongsTo('video-category', { async: false }),
  user: belongsTo('user', {
    async: false,
    inverse: 'projectdigests',
  }),

  /**
   * If we have an embed that supports autoplay, return it in the autoplay format, otherwise just return the URI unchanged.
   *
   * @type {String}
   */
  embedWithAutoplay: computed('oembed.videoEmbedUrl', function() {
    let uri = this.oembed?.videoEmbedUrl;
    if (!(/(vimeo|youtube)/i.test(uri))) {
      //not a vimeo or youtube uri, so just return it.
      return uri;
    }

    //otherwise, we'll attach an autoplay query param to the embed uri before inserting in the doc.
    let parsedUri = parseUri(uri),
      query = parsedUri.query;

    if (query) {
      query.autoplay = '1';
    } else {
      parsedUri.query = {
        autoplay : '1'
      };
    }

    //clear search to force the urlBuilder to use the 'query' object
    parsedUri.search = '';
    uri = formatUri(parsedUri);
    return uri;
  }),

  /**
   * Get a thumbnail for this link or return '' if one is not found.
   *
   * @type {String}
   */
  thumbnailForLink: computed('link', function() {
    let link = this.get('link');
    if (!link || !isUrl(link)) {
      return '';
    }

    return this.get('videoThumbnailService').getThumb(link);
  }),

  /** @type {Boolean} Whether this project was done on our platform. */
  wasDoneOnPlatform: bool('application.id'),

  /** @type {Boolean} Whether the user can delete this project. */
  canDelete: not('wasDoneOnPlatform'),

  mostRecentProjectDigestVerification: computed('projectdigestVerifications.@each.updatedAt', function() {
    return this.projectdigestVerifications.sortBy('updatedAt').firstObject;
  }),

  /**
   * *** note *** you must have pulled in the application relationship for this to be accurate.
   *
   * @type {Boolean}    True if this is a pending publication.
   */
  isPendingPublication: computed('link', 'application', function() {
    return this.get('application.id') && !this.get('link');
  }),

  /**
   * Requires oembed include
   * @type {Boolean}    True when this has an embeddable video
   */
  isEmbedVideo: computed('oembed.{embedUrl,isBrokenVideo}', function() {
    return Boolean(!this.oembed?.isBrokenVideo && this.oembed?.embedUrl);
  }),

  /**
   * Requires oembed include
   * @type {Boolean}    True when this has a linkable video to an external site.
   */
  isLinkableVideo: computed('link', 'oembed.url', function() {
    return Boolean(this.oembed?.url ?? this.link);
  }),

  /**
   * Requires oembed include
   * @type {String}    aspect-ratio inline css
   */
  oembedAspectRatioStyle: computed('oembed.{width,height}', function() {
    const { oembed } = this;
    // default if property unavailable
    if (!oembed || !oembed.width || !oembed.height) {
      return htmlSafe(`aspect-ratio: 16 / 9;`);
    }

    return htmlSafe(`aspect-ratio: ${oembed.width} / ${oembed.height};`);
  }),

  /**
   * @type {Boolean}
   */
  shouldEmbed: computed('usesHttpEmbedLink', function() {
    let embed = this.get('embed');
    let usesHttpEmbedLink = this.get('usesHttpEmbedLink');
    return Boolean(embed && !usesHttpEmbedLink);
  }),

  /**
   * @type {Boolean}    True if we have a valid 'embed'
   */
  usesHttpEmbedLink: computed('embed', function() {
    let embed = this.get('embed');
    return Boolean(embed && embed.substr(0, 7) === 'http://');
  }),
});
